/**
 * Resizable Sidebar Hook
 * 
 * Implements drag-to-resize functionality for sidebars with:
 * - Smooth drag interaction with proper cursor feedback
 * - Width constraints (min/max)
 * - localStorage persistence per sidebar type
 * - Performance-optimized with requestAnimationFrame
 * - Mobile-aware (no resize on mobile)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '@/lib/logger';

interface UseResizableSidebarOptions {
  sidebarId: string;
  defaultWidth: number;
  minWidth?: number;
  maxWidth?: number;
  enabled?: boolean;
  /** When true, dragging left increases width (for right-docked sidebars) */
  invertDrag?: boolean;
}

interface UseResizableSidebarReturn {
  width: number;
  isResizing: boolean;
  handleResizeStart: (e: React.MouseEvent | React.TouchEvent) => void;
  /** @deprecated Prefer handleResizeStart — kept for existing callers */
  handleMouseDown: (e: React.MouseEvent) => void;
  resizeHandleProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
    style: React.CSSProperties;
    className: string;
  };
}

const STORAGE_PREFIX = 'flowstate.sidebar.width.';

export const useResizableSidebar = ({
  sidebarId,
  defaultWidth,
  minWidth = 280,
  maxWidth = 800,
  enabled = true,
  invertDrag = false,
}: UseResizableSidebarOptions): UseResizableSidebarReturn => {
  // Load persisted width or use default (always read storage so toggling enabled later preserves user sizing)
  const [width, setWidth] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(`${STORAGE_PREFIX}${sidebarId}`);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) {
          return parsed;
        }
      }
    } catch (error) {
      logger.warn('Failed to load sidebar width from localStorage:', { error });
    }
    return defaultWidth;
  });

  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  const rafRef = useRef<number | undefined>(undefined);

  // Persist width changes
  useEffect(() => {
    if (!enabled) return;
    
    try {
      localStorage.setItem(`${STORAGE_PREFIX}${sidebarId}`, width.toString());
    } catch (error) {
      logger.warn('Failed to save sidebar width to localStorage:', { error });
    }
  }, [width, sidebarId, enabled]);

  // Clamp width when constraints change (e.g. viewport resize / mode switch)
  useEffect(() => {
    setWidth((prev) => {
      if (prev < minWidth) return minWidth;
      if (prev > maxWidth) return maxWidth;
      return prev;
    });
  }, [minWidth, maxWidth]);

  const applyResizeDelta = useCallback(
    (clientX: number) => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const deltaX = invertDrag
          ? startXRef.current - clientX
          : clientX - startXRef.current;
        const newWidth = Math.min(
          Math.max(startWidthRef.current + deltaX, minWidth),
          maxWidth,
        );
        setWidth(newWidth);
      });
    },
    [minWidth, maxWidth, invertDrag],
  );

  // Handle mouse / touch move during resize
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
      applyResizeDelta(e.clientX);
    },
    [isResizing, applyResizeDelta],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isResizing || e.touches.length === 0) return;
      applyResizeDelta(e.touches[0].clientX);
    },
    [isResizing, applyResizeDelta],
  );

  // Handle mouse up to end resize
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    
    // Cancel any pending animation frame
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }
  }, []);

  // Setup/cleanup event listeners
  useEffect(() => {
    if (!isResizing || !enabled) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleMouseUp);

    // Prevent text selection during resize
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isResizing, enabled, handleMouseMove, handleTouchMove, handleMouseUp]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!enabled) return;

      e.preventDefault();
      e.stopPropagation();

      const clientX =
        "touches" in e ? e.touches[0].clientX : e.clientX;
      startXRef.current = clientX;
      startWidthRef.current = width;
      setIsResizing(true);
    },
    [width, enabled],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleResizeStart(e);
    },
    [handleResizeStart],
  );

  return {
    width: enabled ? width : defaultWidth,
    isResizing,
    handleResizeStart,
    handleMouseDown,
    resizeHandleProps: {
      onMouseDown: handleMouseDown,
      onTouchStart: handleResizeStart,
      style: {
        cursor: enabled ? 'ew-resize' : 'default',
      },
      className: enabled ? 'resize-handle' : '',
    },
  };
};

export default useResizableSidebar;
